import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { AspectRatio } from 'js/src/client';
import { useTemplateRowSizing } from 'js/src/components/app/template-preview/useTemplateRowSizing';
import Button, { BUTTON_COLORS, BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import TemplatePreviewButton from 'js/src/components/common/ui/TemplatePreviewButton';
import {
    getCategoryById,
    getTemplateDefaultAspectRatio,
    getTemplateIdsForACategory,
} from 'js/src/data/templates';
import type { TemplateCategoryId } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import type { StoredTemplate } from 'js/src/data/templates';

type TemplateCategoryPreviewProps = {
    categoryId: TemplateCategoryId;
    onTemplateClick: (template: StoredTemplate) => void;
    getTemplateCategoryRefs?: () => Map<TemplateCategoryId, HTMLElement>;
    selectedTemplateId?: string;
    hidePreviewButton?: boolean;
};

const TemplateCategoryPreview = React.memo(function TemplateCategoryPreview({
    onTemplateClick,
    categoryId,
    getTemplateCategoryRefs,
    selectedTemplateId,
    hidePreviewButton,
}: TemplateCategoryPreviewProps) {
    const category = useSelector((state: RootState) => getCategoryById(state, categoryId));
    const templateIds = useSelector((state: RootState) =>
        getTemplateIdsForACategory(state, categoryId),
    );
    const { rowAspectRatio, isMixed } = useTemplateRowSizing(
        templateIds,
        getTemplateDefaultAspectRatio,
    );
    const name = category.display_name;

    const [showingAll, setShowingAll] = React.useState(false);
    const [columnsPerRow, setColumnsPerRow] = React.useState<number | null>(null);
    const gridRef = React.useRef<HTMLDivElement>(null);

    // Measure how many columns the CSS grid actually renders so we can cap the display at exactly
    // two rows. The grid uses auto-fill with minmax, so column count varies with container width
    // and aspect ratio — a static cap would cut off mid-row or hide too few templates.
    React.useLayoutEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const measure = () => {
            const children = grid.children;
            if (children.length < 2) {
                setColumnsPerRow(null);
                return;
            }
            // Count how many children share the same top offset as the first child — that's one row.
            const firstTop = (children[0] as HTMLElement).getBoundingClientRect().top;
            let count = 0;
            for (let i = 0; i < children.length; i++) {
                if ((children[i] as HTMLElement).getBoundingClientRect().top !== firstTop) break;
                count++;
            }
            setColumnsPerRow(count > 0 ? count : null);
        };

        // Re-measure whenever the grid container resizes (e.g. browser resize, sidebar open/close).
        const observer = new ResizeObserver(measure);
        observer.observe(grid);
        measure();
        return () => observer.disconnect();
    }, [templateIds]);

    const displayCap = columnsPerRow !== null ? columnsPerRow * 2 : null;
    const hasMoreThanCap = displayCap !== null && templateIds.length > displayCap;
    const displayedTemplateIds =
        showingAll || displayCap === null ? templateIds : templateIds.slice(0, displayCap);

    const handleToggle = React.useCallback(() => setShowingAll(!showingAll), [showingAll]);

    if (templateIds.length === 0) {
        return null;
    }

    return (
        <div
            className="template-category mb-l5-64"
            data-testid={name}
            ref={(node) => {
                if (!getTemplateCategoryRefs) return;
                const templateCategoryRefsMap = getTemplateCategoryRefs();
                if (node) {
                    templateCategoryRefsMap.set(categoryId, node);
                } else {
                    templateCategoryRefsMap.delete(categoryId);
                }
            }}
        >
            <div className="template-category-title">{name}</div>
            <div
                ref={gridRef}
                className={classNames('template-grid', {
                    'template-grid--square': rowAspectRatio === AspectRatio.SQUARE,
                    'template-grid--fourfive': rowAspectRatio === AspectRatio.FOURFIVE,
                    'template-grid--vertical': rowAspectRatio === AspectRatio.VERTICAL,
                })}
            >
                {displayedTemplateIds.map((templateId) => (
                    <TemplatePreviewButton
                        onClick={onTemplateClick}
                        templateId={templateId}
                        selected={selectedTemplateId === templateId}
                        hidePreviewButton={hidePreviewButton}
                        forceSquareContain={isMixed}
                        key={templateId}
                    />
                ))}
            </div>
            {hasMoreThanCap && (
                <div className="d-flex justify-content-center mt-l5-16">
                    <Button
                        variant={BUTTON_VARIANTS.OUTLINE}
                        color={BUTTON_COLORS.TERTIARY}
                        iconRight={showingAll ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        onClick={handleToggle}
                        text={showingAll ? 'Show less' : 'Show all'}
                    />
                </div>
            )}
        </div>
    );
});

export default TemplateCategoryPreview;
